/**
 * Module: screens/onboarding/steps/WelcomeStep
 * Role: Buyer onboarding Step 1 — 3-slide welcome carousel with dot indicator.
 *       Single component, internal slide driven by useOnboardingStore (no sub-routes).
 *       ADR-023: no window.Telegram access, no platform literals.
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - providers/PlatformProvider:usePlatform
 *   - screens/onboarding/store:useOnboardingStore
 *   - screens/flow/atoms/Frame, Caveat, CtaBar
 *   - screens/flow/atoms/Icons:Check
 *   - styles/tokens:P, T
 *
 * Used by: screens/onboarding/BuyerOnboardingScreen.tsx (route path="welcome")
 * Glossary: ai/glossary/buyer.md
 */

// ADR-023: no window.Telegram / platform literals outside src/platform/
// §7: no openExternal(BOT_LINK)

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../../../providers/PlatformProvider';
import { useOnboardingStore } from '../store';
import { Caveat } from '../../flow/atoms/Caveat';
import { PressBtn } from '../../../components/atoms/PressBtn';
import { Check } from '../../flow/atoms/Icons';
import { P, T } from '../../../styles/tokens';

// ─── Slide art ──────────────────────────────────────────────────────────────

function ArtFind() {
  return (
    <div style={{ width: 280, height: 260, position: 'relative', margin: '0 auto' }}>
      <div style={{
        position: 'absolute', top: 30, left: 40, width: 180, height: 180,
        borderRadius: '50%', border: `8px solid ${P.ink}`,
        boxShadow: '0 14px 30px rgba(45,37,24,0.18)',
        background: P.cream, overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: 30, left: 30, right: 30, bottom: 30,
          background: '#fff', borderRadius: 12, padding: 12,
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <div style={{ height: 60, borderRadius: 8,
            background: 'radial-gradient(circle at 30% 30%, hsl(18,30%,90%), hsl(48,30%,75%))' }} />
          <div style={{ height: 6, width: '70%', background: P.line, borderRadius: 3 }} />
          <div style={{ height: 6, width: '50%', background: P.line, borderRadius: 3 }} />
          <div style={{ alignSelf: 'flex-end', fontFamily: T.fontAccent, fontSize: 22,
            color: P.terraDark, fontWeight: 700, lineHeight: 1, marginTop: 'auto' }}>+380</div>
        </div>
      </div>
      <div style={{
        position: 'absolute', top: 195, left: 195, width: 14, height: 90,
        background: P.ink, borderRadius: 8, transform: 'rotate(40deg)',
        transformOrigin: 'top left',
      }} />
      <div style={{ position: 'absolute', top: 8, right: 4,
        padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
        background: P.terraSoft, color: P.terraDark,
      }}>3 шага · 30 сек</div>
    </div>
  );
}

function ArtSteps() {
  const steps = [
    { n: 1, label: 'Найти',    color: P.success },
    { n: 2, label: 'Заказать', color: P.success },
    { n: 3, label: 'Получить', color: P.terracotta, active: true },
    { n: 4, label: 'Чек',      color: '#D9D3C3' },
    { n: 5, label: 'Отзыв',    color: '#D9D3C3' },
  ];
  return (
    <div style={{ width: 280, height: 260, display: 'flex', flexDirection: 'column',
      gap: 10, justifyContent: 'center', margin: '0 auto' }}>
      {steps.map((s) => (
        <div key={s.n} style={{
          display: 'flex', alignItems: 'center', gap: 14,
          background: '#fff', borderRadius: 14, padding: '10px 14px',
          border: `1px solid ${P.line}`,
          boxShadow: '0 4px 12px rgba(45,37,24,0.04)',
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 14, background: s.color,
            color: s.color === '#D9D3C3' ? P.muted : '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 13, flexShrink: 0,
            border: s.active ? `3px solid ${P.terraSoft}` : 'none',
          }}>
            {s.n <= 2 ? <Check size={14} /> : s.n}
          </div>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: P.ink }}>{s.label}</div>
          {s.active && <Caveat color={P.terraDark} rot={-4} size={16}>сейчас</Caveat>}
        </div>
      ))}
    </div>
  );
}

function ArtWallet() {
  return (
    <div style={{ width: 280, height: 260, position: 'relative', margin: '0 auto' }}>
      <div style={{
        position: 'absolute', top: 50, left: 30, right: 30, bottom: 50,
        background: P.ink, borderRadius: 22, padding: 22, color: '#fff',
        boxShadow: '0 24px 50px rgba(45,37,24,0.30)',
      }}>
        <div style={{ fontSize: 11, opacity: 0.6 }}>копилка</div>
        <div style={{ fontFamily: T.fontDisplay, fontSize: 38, fontWeight: 700,
          letterSpacing: -0.5, lineHeight: 1, marginTop: 4 }}>
          1 240 <span style={{ fontSize: 22, opacity: 0.55 }}>₽</span>
        </div>
        <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>можно вывести</div>
        <div style={{ marginTop: 14, height: 32, borderRadius: 16, background: '#fff',
          color: P.ink, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 600 }}>Вывести через СБП</div>
      </div>
      <div style={{ position: 'absolute', top: 18, left: 60, fontFamily: T.fontAccent,
        fontSize: 30, color: P.terraDark, fontWeight: 700, transform: 'rotate(-8deg)' }}>+380</div>
      <div style={{ position: 'absolute', top: 30, right: 30, fontFamily: T.fontAccent,
        fontSize: 24, color: P.jade, fontWeight: 700, transform: 'rotate(6deg)' }}>+250</div>
      <div style={{ position: 'absolute', bottom: 14, left: 50, fontFamily: T.fontAccent,
        fontSize: 22, color: P.terraDark, fontWeight: 700, transform: 'rotate(4deg)' }}>+120</div>
      <div style={{ position: 'absolute', bottom: 8, right: 40, fontFamily: T.fontAccent,
        fontSize: 26, color: P.jade, fontWeight: 700, transform: 'rotate(-6deg)' }}>+460</div>
    </div>
  );
}

// ─── Slide metadata ──────────────────────────────────────────────────────────

const SLIDES = [
  {
    art: 'find' as const,
    caveat: 'привет!',
    caveatRot: -6,
    title: 'Покупай — возвращай часть денег',
    body: 'Мы находим товары, на которых бренды дают кешбэк. Вы покупаете как обычно, а часть стоимости возвращается в копилку.',
    bg: P.bg,
  },
  {
    art: 'steps' as const,
    caveat: '5 шагов',
    caveatRot: 8,
    title: 'Пять простых шагов на каждое задание',
    body: 'Найти — заказать — получить — чек — отзыв. Мы напомним и подскажем на каждом шаге.',
    bg: '#F4EFE0',
  },
  {
    art: 'wallet' as const,
    caveat: 'ваши ₽',
    caveatRot: -4,
    title: 'Копи и выводи на карту',
    body: 'Каждое принятое задание добавляется в копилку. Когда наберётся 1 000 ₽ — выводи через СБП за 1 минуту.',
    bg: '#EFEDE0',
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function WelcomeStep() {
  const navigate = useNavigate();
  const platform = usePlatform();
  const { slide, setSlide } = useOnboardingStore();
  // Ref-guard: track the last slide index we registered back-button for
  const backRegisteredRef = useRef<number>(-1);

  const s = SLIDES[slide];
  const isLast = slide === SLIDES.length - 1;

  // Register platform back-button with custom handler per slide.
  // On slide > 0: decrement. On slide = 0: navigate(-1) (default leave behavior).
  useEffect(() => {
    if (backRegisteredRef.current === slide) return;
    backRegisteredRef.current = slide;

    if (slide > 0) {
      platform.showBackButton(() => {
        setSlide((slide - 1) as 0 | 1 | 2);
      });
    } else {
      platform.showBackButton(() => navigate(-1));
    }
    return () => {
      platform.hideBackButton();
      backRegisteredRef.current = -1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide]);

  function goNext() {
    if (slide < SLIDES.length - 1) {
      platform.hapticImpact('light');
      setSlide((slide + 1) as 1 | 2);
    } else {
      navigate('../city');
    }
  }

  function skip() {
    navigate('../city');
  }

  function goToSlide(i: number) {
    if (i === slide) return;
    platform.hapticImpact('light');
    setSlide(i as 0 | 1 | 2);
  }

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      overflow: 'hidden', background: s.bg, transition: 'background 0.3s ease' }}>

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px 0', minHeight: 44, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 16, fontWeight: 700,
            color: P.ink }}>Awardy</span>
        </div>
        <button onClick={skip} style={{ background: 'none', border: 'none',
          fontFamily: T.fontBody, fontSize: 14, color: P.muted,
          fontWeight: 500, cursor: 'pointer', padding: '4px 8px' }}>
          Пропустить
        </button>
      </div>

      {/* Art area */}
      <div key={`art-${slide}`} style={{ flex: 1, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px 16px 0',
        animation: 'fadeIn 0.4s ease' }}>
        {s.art === 'find' && <ArtFind />}
        {s.art === 'steps' && <ArtSteps />}
        {s.art === 'wallet' && <ArtWallet />}
      </div>

      {/* Copy */}
      <div style={{ padding: '0 28px', textAlign: 'center', position: 'relative', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: -28, left: '50%',
          transform: 'translateX(-50%)' }}>
          <Caveat color={P.terraDark} rot={s.caveatRot} size={24}>{s.caveat}</Caveat>
        </div>
        <div key={`title-${slide}`} style={{ fontFamily: T.fontDisplay, fontSize: 26,
          fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.2, color: P.ink,
          animation: 'fadeIn 0.4s ease' }}>
          {s.title}
        </div>
        <div key={`body-${slide}`} style={{ fontSize: 14, color: P.muted, marginTop: 12,
          lineHeight: 1.55, animation: 'fadeIn 0.5s ease' }}>
          {s.body}
        </div>
      </div>

      {/* Dot indicator */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: '24px 0 18px',
        flexShrink: 0 }}>
        {SLIDES.map((_, i) => (
          <div key={i} onClick={() => goToSlide(i)} style={{
            width: i === slide ? 22 : 7, height: 7, borderRadius: 4,
            background: i === slide ? P.terracotta : 'rgba(45,37,24,0.18)',
            transition: 'all 0.3s ease', cursor: 'pointer',
          }} />
        ))}
      </div>

      {/* CTA */}
      <div style={{ padding: '0 16px 30px', flexShrink: 0 }}>
        <PressBtn onClick={goNext} bg={P.terracotta}>
          {isLast ? 'Поехали  →' : 'Дальше'}
        </PressBtn>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
